import React from 'react'
import Nav from '../../src/components/Nav/wallet'
import { urls } from '../../src/utils/config'
import Footer from '../../src/components/Footer'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles } from '@material-ui/core/styles'
import Background from '../../src/components/Mint/Background'
import MintCard from '../../src/components/Mint/MintCard'
import { Typography, Grid } from '@material-ui/core'
import FAQSection from '../../src/components/Product/FAQSection'
import BenefitsSection from '../../src/components/Community/Benefits'
import { useWeb3Context } from '../../src/contexts/Web3Context'
import { commify } from 'ethers/lib/utils'
import { discordLink } from '../../src/utils/string'

const faqs = [
  {
    question: 'How do I mint the NFT?',
    answer: `[Click this link to see a tutorial on how to mint](https://cryptotesters.notion.site/cryptotesters/How-to-mint-a-Cryptotesters-NFT-796052775f9a43d19b20a1c6962b203d).`,
  },
  {
    question: 'Why is the community membership tokenized?',
    answer:
      'We believe a membership NFT is more powerful than a subscription. Unlike a subscription you can always decide to exit the community and sell your  NFT - sometimes even at profit.',
  },
  {
    question: 'What will happen with the money raised?',
    answer:
      'The bulk of funds will go into a community treasury managed by community members to grow the platform and community. Other parts will go to charity artist compensation and lastly some will be reserved for Cryptotesters founders.',
  },
  {
    question: 'Who was eligible for the free mint?',
    answer:
      'The Cryptotesters community has been existing for over 1 year. About 1200 early members were eligible for the free mint.',
  },
  {
    question: 'Why is the mint happening on Layer-2?',
    answer:
      'We wanted to do our part in kickstarting Layer-2 adoption for NFT’s. We hope you’ll enjoy the significantly lower transaction fees for minting and trading the membership NFT’s.',
  },
  {
    question: 'How do I join the Community?',
    answer: `Once you have bought the membership, [join our Discord here](${discordLink()}) and follow the steps to connect your wallet and prove ownership over a Cryptotesters NFT. Once that’s done, you’ll have full access.`,
  },
]
const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
  },
  aboveFold: {
    minHeight: '100vh',
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
      padding: '0 4rem',
    },
    [theme.breakpoints.down('sm')]: {
      background:
        'linear-gradient(0deg, rgba(0, 0, 0, 0.82), rgba(0, 0, 0, 0.82)), radial-gradient(142.16% 142.16% at 33.37% -35.39%, #E277EE 8.93%, #5E2FBB 73.54%, #382751 100%) /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */',
    },
  },
  contentSection: {
    margin: '40px 0',
    padding: '40px',
    [theme.breakpoints.up('md')]: {
      margin: '80px 0',
      padding: '40px 100px',
    },
  },
  title: {
    color: 'white',
    textAlign: 'left',
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '32px',
      maxWidth: '100%',
      margin: '0 auto 0 auto',
    },
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
  mintStats: {
    '& .MuiTypography-subtitle2': {
      color: 'rgba(255, 255, 255, 0.46)',
      fontWeight: 600,
      '& .white': {
        color: 'white',
      },
    },
  },

  subtitle: {
    textAlign: 'left',
    marginTop: '20px',
    marginBottom: '30px',
    position: 'relative',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '16rem',
    [theme.breakpoints.down('xs')]: {},
  },
  heroTextSection: {
    width: '100%',
  },
  section: {},
  sectionTitle: {
    color: 'black',
    letterSpacing: '-0.02em',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: '80px 0',
  },
  heroCTAs: {
    width: '100%',
    textAlign: 'center',
    padding: '2rem 0',
    '& button': {
      margin: '0 1rem',
      textTransform: 'none',
      fontSize: '1rem',
    },
  },
  guideSection: {
    margin: '8rem 0 4rem',
  },
  imageContainer: {
    position: 'relative',
    height: '4rem',
  },
  communityImage: {
    background: 'rgba(0, 0, 0, 0.04)',
    borderRadius: '2rem',
    padding: '2rem',
    width: 'fit-content',
    alignSelf: 'center',
  },
  why: {
    display: 'flex',
    minHeight: '14rem',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginBottom: '4rem',
  },
  discordButton: {
    width: 'object-fit',
  },
  guideCard: {
    padding: '1rem',
    borderRadius: '.8rem',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(0, 0, 0, 0.04)',
    margin: '2rem 0',
  },
  guideButton: {
    textTransform: 'none',
  },
  communityImageContainer: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'grid',
    },
  },
  mainPFP: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  grid: {
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
}))

function Mint(): JSX.Element {
  const classes = useStyles({})
  const { currentTokenId } = useWeb3Context()
  const totalNFTs = 2000

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.home,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters | The #1 Crypto Product Comparison Platform`}
            description={`Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`}
            openGraph={{
              url: `/community/mint`,
              title: `Cryptotesters | The #1 Crypto Product Comparison Platform`,
              description: `Cryptotesters is your go-to resource to navigate the crypto universe. We help you find the best cryptocurrency exchanges, wallets and much more.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/mint.jpeg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com PFP`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/mint.jpeg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com PFP`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav variant="light" />
          <div className={classes.root}>
            <div className={classes.aboveFold}>
              <Background size="xxl" />
              <div className={classes.heroContent}>
                <Grid container spacing={4} className={classes.grid}>
                  <Grid item xs={12} md={5}>
                    <div className={`${classes.heroTextSection} ${classes.mintStats}`}>
                      <Typography variant="h4" className={classes.title}>
                        Mint Stats
                      </Typography>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        The testers community has <span className="white">2,000+ members.</span> The
                        community is made up of builders, investors, researchers and crypto
                        enthusiasts.
                      </Typography>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        Membership in this community is tokenized in the form of an NFT.
                      </Typography>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        Once you have purchased a membership NFT you will be able to enter the
                        community Discord benefit from all community perks.
                      </Typography>
                      <Typography variant="subtitle2" className={classes.subtitle}>
                        There are just <span className="white"> 2,000 </span> NFTs for mint.
                      </Typography>
                      {currentTokenId ? (
                        <Typography variant="subtitle2" className={classes.subtitle}>
                          <span className="white"> {commify(currentTokenId)} / 2,000 </span> NFTs
                          have been minted.
                        </Typography>
                      ) : null}
                      {currentTokenId ? (
                        <Typography variant="subtitle2" className={classes.subtitle}>
                          There are{' '}
                          <span className="white">{commify(totalNFTs - currentTokenId)} </span>
                          left to mint.
                        </Typography>
                      ) : null}
                    </div>
                  </Grid>
                  <Grid item xs={12} md={7} justify="center" container>
                    <MintCard />
                  </Grid>
                </Grid>
              </div>
            </div>

            <div className={classes.contentSection}>
              <BenefitsSection />
            </div>
          </div>
          <FAQSection faqs={faqs} />
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export default Mint
